/**
 * Header — Barre supérieure identique à la v1 PHP
 *
 * Fond anthracite, logo doré ✧, recherche, liens Cours/Clients/Déconnexion
 */

import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext.js';
import { colors } from '../../styles/tokens.js';

interface HeaderProps {
  onMenuToggle: () => void;
}

export function Header({ onMenuToggle }: HeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  /** Page courante pour highlight du lien actif */
  const currentPage = location.pathname.startsWith('/clients') ? 'clients' : 'cours';

  // Raccourci clavier Ctrl+K pour focus la recherche
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (searchQuery.trim().length >= 2) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  }

  return (
    <header style={{
      background: '#1a1a1a',
      color: '#fff',
      padding: '0 2rem',
      height: 60,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      flexShrink: 0,
    }}>
      {/* Hamburger (mobile) */}
      <button
        onClick={onMenuToggle}
        aria-label="Ouvrir le menu"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          padding: 8, color: '#fff', fontSize: 22, display: 'none',
        }}
        className="mobile-menu-btn"
      >
        <i className="bi bi-list" />
      </button>

      {/* Recherche */}
      <div style={{ flex: 1, maxWidth: 450 }} className="search-container-header">
        <form onSubmit={handleSearch} style={{
          display: 'flex', alignItems: 'center',
          background: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 25, padding: '8px 16px',
        }}>
          <i className="bi bi-search" style={{ color: 'rgba(255,255,255,0.5)', marginRight: 10 }} />
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher dans les fiches..."
            style={{
              background: 'none', border: 'none', color: '#fff',
              width: '100%', outline: 'none', fontSize: '0.9rem',
              fontFamily: 'inherit',
            }}
          />
        </form>
      </div>

      {/* Liens navigation — identique v1 */}
      <nav style={{ display: 'flex', gap: '0.5rem' }}>
        <Link
          to="/dashboard"
          style={{
            color: currentPage === 'cours' ? '#1a1a1a' : 'rgba(255,255,255,0.8)',
            padding: '8px 16px', borderRadius: 6,
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: '0.9rem', textDecoration: 'none',
            background: currentPage === 'cours' ? colors.accent : 'transparent',
            transition: 'all 0.2s',
          }}
        >
          <i className="bi bi-book" />
          <span className="nav-link-label">Cours</span>
        </Link>
        <Link
          to="/clients"
          style={{
            color: currentPage === 'clients' ? '#1a1a1a' : 'rgba(255,255,255,0.8)',
            padding: '8px 16px', borderRadius: 6,
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: '0.9rem', textDecoration: 'none',
            background: currentPage === 'clients' ? colors.accent : 'transparent',
            transition: 'all 0.2s',
          }}
        >
          <i className="bi bi-people" />
          <span className="nav-link-label">Clients</span>
        </Link>
        <button
          onClick={handleLogout}
          style={{
            color: 'rgba(255,255,255,0.8)',
            padding: '8px 16px', borderRadius: 6,
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: '0.9rem', background: 'none', border: 'none',
            cursor: 'pointer', fontFamily: 'inherit',
            transition: 'all 0.2s',
          }}
        >
          <i className="bi bi-box-arrow-right" />
        </button>
      </nav>

      {/* Styles responsive — identique v1 */}
      <style>{`
        .mobile-menu-btn { display: none !important; }
        @media (max-width: 768px) {
          .mobile-menu-btn { display: block !important; }
          .search-container-header { display: none !important; }
          .nav-link-label { display: none; }
        }
      `}</style>
    </header>
  );
}
